import { createAction, createAsyncAction } from "typesafe-actions";

import type {
    Signal,
    SignalId,
    SignalSource,
    SignalType,
} from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { FetchError } from "app/pages/sales-intelligence/types";

import type CountPerSignalId from "../models/miscellaneous/CountPerSignalId";
import type { SignalsDescription } from "../models/miscellaneous/SignalsDescription";
import type SignalsGroupedById from "../models/miscellaneous/SignalsGroupedById";

export const appliedSignalsChangedAction = createAction("@@si/signals/applied-signals-changed")<readonly SignalId[]>();

export const addAppliedSignalsAction = createAction("@@si/signals/applied-signals-added")<readonly SignalId[]>();

export const setShouldAutoApplyCreatedSignal = createAction(
    "@@si/signals/should-auto-apply-created-signal-changed",
)<boolean>();

export const createdSignalAutoAppliedAction = createAction("@@si/signals/created-signal-auto-applied")<
    Readonly<Signal>
>();

export const clearLastAutoAppliedCreatedSignalAction = createAction(
    "@@si/signals/clear-last-auto-applied-created-signal",
)<void>();

/**
 * Event action dispatched when a draft signal is created AND immediately applied
 * This action does not modify state (handled by createDraftSignalSuccess + addAppliedSignalsAction)
 * It serves as a semantic notification for middleware
 */
export const draftSignalCreatedAndApplied = createAction("@@si/signals/draft-signals-created-and-applied")<{
    signalId: SignalId;
}>();

export const removeAppliedSignalsAction = createAction("@@si/signals/applied-signals-removed")<readonly SignalId[]>();

export const isDropdownOpenAction = createAction("@@si/signals/is-dropdown-open-changed")<boolean>();

export const isSignalsManagementOpenAction = createAction("@@si/signals/is-signals-management-open-changed")<boolean>();

export const setPreselectedSignalType = createAction("@@si/signals/set-preselected-signal-type")<SignalType | null>();

/**
 * Opens the signals management modal AND sets the preselected signal type in a
 * single state transition — so there is no intermediate render where the modal
 * is open but the preselected type is still null. Pass `null` to open unscoped.
 */
export const openSignalsManagementForType = createAction("@@si/signals/open-signals-management-for-type")<SignalType>();

export const setInitialSelectedSignalType = createAction(
    "@@si/signals/set-initial-selected-signal-type",
)<SignalType | null>();

export const setManagementEditSignalId = createAction("@@si/signals/set-management-edit-signal-id")<SignalId | null>();

export const setManagementDeleteSignalId = createAction(
    "@@si/signals/set-management-delete-signal-id",
)<SignalId | null>();

export const setSignalIdForHighlight = createAction("@@si/signals/set-signal-id-for-highlight")<SignalId | null>();

export const setPreselectedAccountOverviewSignalType = createAction(
    "@@si/signals/set-preselected-account-overview-signal-type",
)<SignalType | null>();

export const areSignalsAvailableChangedAction = createAction("@@si/signals/are-signals-available-changed")<boolean>();

export const setSignalsSourceAction = createAction("@@si/signals/set-signals-source")<SignalSource>();
export const resetSignalsSourceAction = createAction("@@si/signals/reset-signals-source")();

export const resetSignalsAvailability = createAction("@@si/signals/reset-signals-availability")<void>();

export const fetchSignalsAsync = createAsyncAction(
    "@@si/signals/all-fetch-started",
    "@@si/signals/all-fetch-succeeded",
    "@@si/signals/all-fetch-failed",
    "@@si/signals/all-fetch-cancelled",
)<void, Readonly<SignalsGroupedById>, FetchError, void>();

export const fetchSignalsDescriptionAsync = createAsyncAction(
    "@@si/signals/description-fetch-started",
    "@@si/signals/description-fetch-succeeded",
    "@@si/signals/description-fetch-failed",
)<void, Readonly<SignalsDescription>, FetchError>();

export const fetchCountPerSignalIdAsync = createAsyncAction(
    "@@si/signals/count-per-signal-id-fetch-started",
    "@@si/signals/count-per-signal-id-fetch-succeeded",
    "@@si/signals/count-per-signal-id-fetch-failed",
)<void, Readonly<CountPerSignalId>, FetchError>();

export const createSignalAsync = createAsyncAction(
    "@@si/signals/signal-create-started",
    "@@si/signals/signal-create-succeeded",
    "@@si/signals/signal-create-failed",
    "@@si/signals/signal-create-cancelled",
)<void, Readonly<Signal>, FetchError, void>();

export const updateSignalAsync = createAsyncAction(
    "@@si/signals/signal-update-started",
    "@@si/signals/signal-update-succeeded",
    "@@si/signals/signal-update-failed",
    "@@si/signals/signal-update-cancelled",
)<void, Readonly<Signal>, FetchError, void>();

export const deleteSignalsAsync = createAsyncAction(
    "@@si/signals/delete-one-started",
    "@@si/signals/delete-one-succeeded",
    "@@si/signals/delete-one-failed",
)<SignalId, SignalId, { signalId: SignalId; error: FetchError }>();

export const createDraftSignalSuccess = createAction("@@si/signals/draft-signal-create-succeeded")<Readonly<Signal>>();

export const saveDraftSignalSuccess = createAction("@@si/signals/draft-signal-save-succeeded")<Readonly<Signal>>();

/**
 * Merges a batch of signals into the catalog (`signalsById`). Dispatched after a saved search is
 * created/updated: the response carries the search's signals, which may have transitioned from
 * draft to permanent on the server, so the stale local copies must be overwritten.
 */
export const signalsUpsertedAction = createAction("@@si/signals/signals-upserted")<readonly Signal[]>();

export const removeExpiredDraftSignalsAction = createAction("@@si/signals/remove-expired-drafts")<{
    expiredSignalIds: readonly SignalId[];
}>();
